import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LogoUploader } from './LogoUploader';

interface OrgSettings {
  firm_name: string;
  logo_url?: string;
  report_primary_color: string;
}

export function OrgSettingsForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  
  const { register, handleSubmit, setValue, watch } = useForm<OrgSettings>({
    defaultValues: {
      firm_name: '',
      report_primary_color: '#0D0D0D'
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('org-logo');
      if (error) throw error;
      
      if (data) {
        setSettings(data);
        setValue('firm_name', data.firm_name || '');
        setValue('report_primary_color', data.report_primary_color || '#0D0D0D');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const onSubmit = async (data: OrgSettings) => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('org_settings')
        .upsert({
          user_id: user.id,
          firm_name: data.firm_name,
          report_primary_color: data.report_primary_color,
          logo_url: settings?.logo_url,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "Organization settings have been updated",
      });

    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Save failed",
        description: "Failed to save organization settings", 
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpdate = (logoUrl: string) => {
    setSettings(prev => ({ ...prev!, logo_url: logoUrl }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('org.settings')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="firm_name">{t('org.firmName')}</Label>
            <Input
              id="firm_name"
              {...register('firm_name', { required: true })}
              placeholder="Enter firm name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="report_color">{t('org.reportColor')}</Label>
            <Input
              id="report_color"
              type="color"
              {...register('report_primary_color')}
              className="w-20 h-10"
            />
          </div>

          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </form>

        <div className="border-t pt-6">
          <LogoUploader 
            currentLogoUrl={settings?.logo_url}
            onLogoUpdate={handleLogoUpdate}
          />
        </div>
      </CardContent>
    </Card>
  );
}